import { useEffect } from "react";
import { Link } from "react-router-dom";
import type { SeoPageData } from "@/data/seoContent";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";
import usePageMeta from "@/hooks/usePageMeta";
import PageShell from "./PageShell";
import SiteCTA from "./SiteCTA";
import RelatedLinks from "./RelatedLinks";
import Reveal from "@/components/base/Reveal";

function HeroStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
        {value}
      </span>
      <span className="text-xs md:text-sm text-foreground-500 leading-snug">{label}</span>
    </div>
  );
}

export default function SeoLandingPage({ data }: { data: SeoPageData }) {
  const { settings } = useSiteSettingsContext();
  const name = settings.siteName || "DersDinle";

  usePageMeta(data.metaTitle, data.metaDescription);

  // Schema.org FAQPage — sorular ve yanıtlar birebir eşleşir.
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: data.faq.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(faqSchema);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.slug]);

  const crumbs = [{ label: "Ana Sayfa", href: "/" }, { label: data.crumb }];

  return (
    <PageShell crumbs={crumbs}>
      {/* Hero */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20 border-b border-background-200">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <span className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700">
              <span className="w-5 h-px bg-accent-500" aria-hidden="true"></span>
              {data.eyebrow}
            </span>
            <h1 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground-950 leading-tight max-w-3xl">
              {data.title}
            </h1>
            <p className="mt-5 text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl">
              {data.subtitle}
            </p>
          </Reveal>
        </div>
      </section>

      {/* İstatistik bandı */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-8 md:py-10 border-b border-background-200">
        <div className="max-w-6xl mx-auto">
          <span className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-foreground-500">
            <span className="w-5 h-px bg-accent-500" aria-hidden="true"></span>
            Rakamlarla {name}
          </span>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {data.stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 60}>
                <HeroStat value={s.value} label={s.label} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Neden / avantajlar */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              Neden {data.crumb.toLowerCase()}?
            </h2>
            <p className="mt-3 text-base text-foreground-600 leading-relaxed max-w-2xl">
              {data.title} avantajları; öğrenmeyi daha planlı, esnek ve ölçülebilir kılar.
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.benefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 50}>
                <div className="flex items-start gap-4 p-5 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors h-full">
                  <span className="inline-flex w-11 h-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${b.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-base text-foreground-950">{b.title}</h3>
                    <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{b.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Nasıl çalışır */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              Nasıl çalışır?
            </h2>
            <p className="mt-3 text-base text-foreground-600 leading-relaxed max-w-2xl">
              Karmaşık değil, net bir akış: her adım seni bir sonrakine hazırlar.
            </p>
          </Reveal>
          <div
            className={`mt-8 grid grid-cols-1 sm:grid-cols-2 ${
              data.steps.length >= 5 ? "lg:grid-cols-5" : "lg:grid-cols-4"
            } gap-4`}
          >
            {data.steps.map((step, i) => (
              <Reveal key={step.title} delay={i * 60}>
                <div className="relative h-full p-5 rounded-lg border border-background-200 bg-background-50">
                  <span className="inline-flex w-8 h-8 items-center justify-center rounded-full text-sm font-bold text-background-50 bg-primary-500">
                    {i + 1}
                  </span>
                  <h3 className="mt-3 font-heading font-semibold text-base text-foreground-950">
                    {step.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* İçerik blokları */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-4xl mx-auto space-y-8">
          {data.blocks.map((block, i) => (
            <Reveal key={block.heading} delay={i * 60}>
              <h3 className="text-xl md:text-2xl font-bold text-foreground-950 tracking-tight">
                {block.heading}
              </h3>
              <p className="mt-3 text-base text-foreground-700 leading-relaxed">{block.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SSS */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              Sık sorulan sorular
            </h2>
          </Reveal>
          <div className="mt-8 space-y-4">
            {data.faq.map((f, i) => (
              <Reveal key={f.question} delay={i * 40}>
                <details className="group rounded-lg border border-background-200 bg-background-50 overflow-hidden">
                  <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-5 py-4">
                    <h4 className="font-heading font-semibold text-base text-foreground-950">
                      {f.question}
                    </h4>
                    <span className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-open:bg-accent-100 group-open:text-accent-700 transition-colors">
                      <i className="ri-add-line group-open:ri-subtract-line" aria-hidden="true"></i>
                    </span>
                  </summary>
                  <p className="px-5 pb-5 text-sm md:text-base text-foreground-700 leading-relaxed">
                    {f.answer}
                  </p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* İlgili içerikler */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
            Keşfetmeye devam et
          </h2>
          <div className="mt-6">
            <RelatedLinks items={data.related} />
          </div>
        </div>
      </section>

      <SiteCTA
        title={data.ctaTitle}
        text={data.ctaText}
        primaryLabel="Hemen Başla"
        primaryHref="/kayit"
        secondaryLabel="Paketleri İncele"
        secondaryHref="/paketler"
      />

      {/* Sayfa içi metin bağlantısı — erişilebilirlik ve iç linkleme */}
      <div className="hidden">
        <Link to={`/${data.slug}`} aria-hidden="true" tabIndex={-1}>
          {data.crumb}
        </Link>
      </div>
    </PageShell>
  );
}